import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md' : 'bg-white/95'
      }`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between h-20">
            <div className="flex items-center gap-3">
              <img 
                src="https://public.readdy.ai/ai/img_res/9ad48baf-41a3-4557-9fcc-d53e3bbf9689.png" 
                alt="PAEZ S.R.L. Logo" 
                className="h-12 w-auto"
              />
              <span className="text-2xl font-bold text-gray-900">PAEZ S.R.L.</span>
            </div>
            <div className="hidden md:flex items-center gap-8">
              <button onClick={() => scrollToSection('home')} className="text-sm font-medium text-gray-700 hover:text-purple-600 transition-colors cursor-pointer">HOME</button>
              <button onClick={() => scrollToSection('vpn')} className="text-sm font-medium text-gray-700 hover:text-purple-600 transition-colors cursor-pointer">VPN APPLICATION</button>
              <button onClick={() => scrollToSection('about')} className="text-sm font-medium text-gray-700 hover:text-purple-600 transition-colors cursor-pointer">ABOUT US</button>
              <button onClick={() => scrollToSection('contact')} className="text-sm font-medium text-gray-700 hover:text-purple-600 transition-colors cursor-pointer">CONTACT</button>
              <button onClick={() => scrollToSection('support')} className="text-sm font-medium text-gray-700 hover:text-purple-600 transition-colors cursor-pointer">SUPPORT</button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="pt-20 min-h-screen flex items-center relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-50 via-blue-50 to-cyan-50"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/5 via-transparent to-transparent"></div>
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <h1 className="text-6xl lg:text-7xl font-serif font-bold text-gray-900 leading-tight">
                Secure Your<br />Digital Life
              </h1>
              <p className="text-xl text-gray-600 leading-relaxed max-w-xl">
                Experience ultimate online privacy and security with PAEZ VPN. Browse anonymously, access content freely, and protect your data with military-grade encryption.
              </p>
              <button onClick={() => scrollToSection('vpn')} className="bg-gray-900 text-white px-8 py-4 rounded-full text-lg font-medium hover:bg-gray-800 transition-all inline-flex items-center gap-2 whitespace-nowrap cursor-pointer">
                Get Started
                <i className="ri-arrow-right-line text-xl"></i>
              </button>
            </div>
            <div className="relative h-[500px] flex items-center justify-center">
              <img 
                src="https://public.readdy.ai/ai/img_res/22ee44d9a5527455b193cae1b4d9cdf7.jpg"
                alt="VPN Security Shield"
                className="w-full h-full object-contain"
              />
            </div>
          </div>
        </div>
      </section>

      {/* VPN Application Features Section */}
      <section id="vpn" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <p className="text-sm font-semibold text-purple-600 tracking-wider uppercase mb-4">FEATURES</p>
            <h2 className="text-5xl font-serif font-bold text-gray-900">Why Choose PAEZ VPN</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: 'ri-shield-check-line',
                title: 'Military-Grade Encryption',
                description: 'Your data is protected with AES-256 encryption, the same standard used by governments and security experts worldwide.',
                gradient: 'from-purple-500 to-pink-500'
              },
              {
                icon: 'ri-speed-line',
                title: 'Lightning Fast Speed',
                description: 'Enjoy seamless browsing, streaming, and downloading with our optimized high-speed servers across the globe.',
                gradient: 'from-blue-500 to-cyan-500'
              },
              {
                icon: 'ri-global-line',
                title: 'Global Server Network',
                description: 'Connect to servers in multiple countries and access content from anywhere in the world without restrictions.',
                gradient: 'from-indigo-500 to-purple-500'
              },
              {
                icon: 'ri-eye-off-line',
                title: 'No-Log Policy',
                description: 'We never track, collect, or share your private data. Your online activity remains completely anonymous.',
                gradient: 'from-pink-500 to-rose-500'
              },
              {
                icon: 'ri-device-line',
                title: 'Multi-Device Support',
                description: 'Protect all your devices with a single account. Compatible with Windows, Mac, iOS, Android, and more.',
                gradient: 'from-cyan-500 to-teal-500'
              },
              {
                icon: 'ri-customer-service-2-line',
                title: '24/7 Customer Support',
                description: 'Our dedicated support team is always ready to help you with any questions or technical issues.',
                gradient: 'from-violet-500 to-purple-500'
              }
            ].map((feature, index) => (
              <div key={index} className="bg-white rounded-3xl p-10 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 cursor-pointer">
                <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${feature.gradient} flex items-center justify-center mb-6`}>
                  <i className={`${feature.icon} text-3xl text-white`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Us Section */}
      <section id="about" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-6">
              <h2 className="text-5xl font-serif font-bold text-gray-900">About PAEZ S.R.L.</h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                PAEZ S.R.L. is a technology company dedicated to developing innovative applications that enhance digital security and user experience. Based in Argentina, we are committed to providing cutting-edge solutions for modern internet users.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                Our flagship VPN application represents our commitment to online privacy and freedom. We believe that everyone deserves secure, unrestricted access to the internet without compromising their personal data.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                With a focus on user-friendly design and robust security features, PAEZ S.R.L. continues to innovate and deliver applications that make a real difference in people's digital lives.
              </p>
            </div>
            <div className="relative h-[500px] rounded-3xl overflow-hidden bg-gradient-to-br from-blue-100 to-purple-100 flex items-center justify-center p-12">
              <img 
                src="https://readdy.ai/api/search-image?query=Minimalist%20line%20art%20illustration%20of%20connected%20devices%20and%20security%20symbols%20in%20white%20outline%20style%2C%20showing%20laptop%20smartphone%20and%20tablet%20protected%20by%20digital%20shield%2C%20clean%20modern%20tech%20illustration%20with%20network%20connections%2C%20simple%20geometric%20shapes%20on%20soft%20gradient%20background&width=600&height=600&seq=about-illustration&orientation=squarish"
                alt="Connected Devices Security"
                className="w-full h-full object-contain"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-amber-50">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <h2 className="text-5xl font-serif font-bold text-gray-900 text-center mb-16">Get In Touch</h2>
          <div className="space-y-12">
            <div className="flex items-start gap-6">
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center flex-shrink-0">
                <i className="ri-mail-line text-2xl text-white"></i>
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2">Email</p>
                <a href="mailto:help@paezsrl.com" className="text-2xl text-gray-900 hover:text-purple-600 transition-colors">help@paezsrl.com</a>
              </div>
            </div>
            <div className="flex items-start gap-6">
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center flex-shrink-0">
                <i className="ri-phone-line text-2xl text-white"></i>
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2">Phone</p>
                <a href="tel:+12058503440" className="text-2xl text-gray-900 hover:text-purple-600 transition-colors">+1 2058503440</a>
              </div>
            </div>
            <div className="flex items-start gap-6">
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center flex-shrink-0">
                <i className="ri-map-pin-line text-2xl text-white"></i>
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2">Address</p>
                <p className="text-2xl text-gray-900">JUAN B. ALBERDI 302<br />Perico, Y4610AGH<br />Argentina</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Support Section */}
      <section id="support" className="py-24 bg-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-serif font-bold text-gray-900 mb-6">Need Help?</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Our support team is here to assist you with any questions or issues you may have. Choose the best way to get help.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-purple-50 rounded-3xl p-12 hover:shadow-lg transition-all duration-300 hover:-translate-y-1 cursor-pointer">
              <div className="w-16 h-16 rounded-2xl bg-purple-500 flex items-center justify-center mb-6">
                <i className="ri-book-open-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Documentation</h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Browse our comprehensive guides and tutorials to get the most out of PAEZ VPN.
              </p>
              <a href="mailto:help@paezsrl.com" className="text-purple-600 font-semibold inline-flex items-center gap-2 hover:gap-3 transition-all">
                Learn More
                <i className="ri-arrow-right-line"></i>
              </a>
            </div>
            <div className="bg-blue-50 rounded-3xl p-12 hover:shadow-lg transition-all duration-300 hover:-translate-y-1 cursor-pointer">
              <div className="w-16 h-16 rounded-2xl bg-blue-500 flex items-center justify-center mb-6">
                <i className="ri-question-answer-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">FAQ</h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Find quick answers to the most commonly asked questions about our VPN service.
              </p>
              <a href="mailto:help@paezsrl.com" className="text-blue-600 font-semibold inline-flex items-center gap-2 hover:gap-3 transition-all">
                View FAQ
                <i className="ri-arrow-right-line"></i>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 rounded-t-[32px] mt-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <img 
                  src="https://public.readdy.ai/ai/img_res/9ad48baf-41a3-4557-9fcc-d53e3bbf9689.png" 
                  alt="PAEZ S.R.L. Logo" 
                  className="h-10 w-auto"
                />
                <span className="text-xl font-bold text-white">PAEZ S.R.L.</span>
              </div>
              <p className="text-gray-400 leading-relaxed">Securing your digital life with innovative VPN technology.</p>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Quick Links</h4>
              <div className="space-y-3">
                <button onClick={() => scrollToSection('home')} className="block text-gray-400 hover:text-white transition-colors cursor-pointer">Home</button>
                <button onClick={() => scrollToSection('vpn')} className="block text-gray-400 hover:text-white transition-colors cursor-pointer">VPN Application</button>
                <button onClick={() => scrollToSection('about')} className="block text-gray-400 hover:text-white transition-colors cursor-pointer">About Us</button>
                <button onClick={() => scrollToSection('contact')} className="block text-gray-400 hover:text-white transition-colors cursor-pointer">Contact</button>
              </div>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Legal</h4>
              <div className="space-y-3">
                <a href="mailto:help@paezsrl.com" className="block text-gray-400 hover:text-white transition-colors">Privacy Policy</a>
                <a href="mailto:help@paezsrl.com" className="block text-gray-400 hover:text-white transition-colors">Terms of Service</a>
              </div>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Connect</h4>
              <div className="space-y-3">
                <a href="mailto:help@paezsrl.com" className="block text-gray-400 hover:text-white transition-colors">help@paezsrl.com</a>
                <a href="tel:+12058503440" className="block text-gray-400 hover:text-white transition-colors">+1 2058503440</a>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-gray-400 text-sm">© 2025 PAEZ S.R.L. All rights reserved</p>
              <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-gray-400 text-sm hover:text-white transition-colors">Powered by Readdy</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}